/**
 * McLeod Pitch Method (MPM).
 *
 * Unlike the ACF2+ tuner in `./tuner.js` — which is left untouched — this returns a *clarity*
 * value alongside the frequency, which is what makes a voicing gate possible at all, and it
 * picks the first NSDF peak above a fraction of the maximum rather than the global maximum,
 * which is the standard fix for autocorrelation's octave errors.
 *
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §4 option 2
 * @see http://www.cs.otago.ac.nz/tartini/papers/A_Smarter_Way_to_Find_Pitch.pdf
 */

import { rms } from '../utilities.js'

export interface PitchDetectionResult {
  /** Detected fundamental, in Hz. */
  frequency: number
  /** NSDF peak height in [0, 1] — how periodic this window is. 1 is a perfect pure tone. */
  clarity: number
}

/**
 * Analysis window, in samples. 2048 samples is ~43 ms at 48 kHz and covers 3.5 periods of an
 * 82 Hz E2, the lowest note this Epic asks a bass to sing. NSDF needs two full periods inside
 * the window to show a peak at all and three for that peak to be stable.
 * @see spec §11.2
 */
export const pitchWindowSize = 2048

/**
 * Hop between analysis windows, in samples: ~21 ms at 48 kHz, ~46 frames per second.
 * A 600 ms note (a crotchet at 100 BPM) yields ~28 frames, ~23 after the 100 ms onset scoop is
 * discarded — comfortably above the >= 15 a median-based note pitch needs, and far above the
 * 2-3 the RAF feature bus would deliver.
 * @see spec §3.2, §11.2
 */
export const pitchHopSize = 1024

/**
 * Accept the *first* NSDF key maximum reaching this fraction of the tallest, not the tallest.
 * At a period-doubled lag the NSDF is nearly as tall as at the true period, so "tallest" is
 * close to a coin flip between a note and the octave below it, and "first" is not. This is the
 * single rule separating this detector from the ACF2+ tuner.
 * @see spec §3.1, §4 option 2
 */
export const nsdfPeakThreshold = 0.93

/**
 * Lowest fundamental searched, in Hz. C2 — a tone below the E2 that is the lowest note this
 * Epic asks a bass to sustain. Not searching longer lags also keeps NSDF's unreliable long-lag
 * tail (few summed terms, values drifting toward +/-1) out of the result.
 */
export const minDetectableHz = 65

/**
 * Highest fundamental searched, in Hz. ~D6, above the top of a trained soprano's tessitura.
 * Bounding the short lags is what stops broadband noise being read as a very high note.
 */
export const maxDetectableHz = 1200

/**
 * Below this window RMS there is no signal to analyse and no frequency is reported at all.
 * Silence must be distinguishable from a quiet note: this is the "nothing heard" floor, an
 * order of magnitude below any live microphone's self-noise.
 */
export const silenceRmsThreshold = 1e-4

/**
 * n'(t) = 2*r'(t) / m'(t), where r' is the autocorrelation and m' the summed squared magnitude
 * over the same overlap. The division bounds the result to [-1, 1] and is what makes the peak
 * height meaningful as a clarity value.
 */
const normalizedSquareDifference = (signal: Float32Array, maxLag: number) => {
  const nsdf = new Float32Array(maxLag + 1)
  for (let lag = 0; lag <= maxLag; lag++) {
    let correlation = 0
    let magnitude = 0
    for (let i = 0; i < signal.length - lag; i++) {
      const a = signal[i]
      const b = signal[i + lag]
      correlation += a * b
      magnitude += a * a + b * b
    }
    nsdf[lag] = magnitude > 0 ? (2 * correlation) / magnitude : 0
  }
  return nsdf
}

/**
 * Lag of the NSDF's first negative-going zero crossing, searched from lag 0.
 *
 * MPM starts its key-maximum search here so that the zeroth peak — the one at lag 0, where every
 * signal correlates perfectly with itself — is excluded. Skipping that guard is only harmless
 * while the crossing (about a quarter period) falls below `minLag`: at `maxDetectableHz / 4` =
 * 300 Hz and under it does not, the scan opens part-way up the zeroth peak, and the search-window
 * edge at `minLag` is recorded as a key maximum standing close to 1. Being first, it then wins
 * the `nsdfPeakThreshold` vote over the true period, and the parabola fitted through a point on
 * that slope puts its vertex outside the window — so a sustained C2 came back as no pitch at all.
 *
 * Searched from 0 rather than from `minLag` on purpose: between 900 and 1200 Hz `minLag` lands on
 * the rising flank of the true period's peak, and a crossing searched from there would skip that
 * peak and report the octave below. The caller clamps the result up to `minLag`, so searching the
 * whole NSDF only ever moves the search start for fundamentals whose zeroth peak outlives it.
 */
const firstZeroCrossing = (nsdf: Float32Array, maxLag: number) => {
  let lag = 0
  while (lag <= maxLag && nsdf[lag] > 0) {
    lag++
  }
  return lag
}

/**
 * A key maximum is the tallest point of one positively-valued run of the NSDF. Taking one
 * candidate per run — rather than every local maximum — is what makes "the first peak above
 * the threshold" mean "the first period-like lag" instead of "the first ripple".
 */
const findKeyMaxima = (nsdf: Float32Array, minLag: number, maxLag: number) => {
  const maxima: number[] = []
  let lag = minLag
  while (lag <= maxLag) {
    while (lag <= maxLag && nsdf[lag] <= 0) {
      lag++
    }
    let best = -1
    while (lag <= maxLag && nsdf[lag] > 0) {
      if (best === -1 || nsdf[lag] > nsdf[best]) {
        best = lag
      }
      lag++
    }
    if (best !== -1) {
      maxima.push(best)
    }
  }
  return maxima
}

/** Fit a parabola through the peak and its neighbours; returns the fractional lag and height. */
const interpolatePeak = (nsdf: Float32Array, lag: number) => {
  const left = nsdf[lag - 1]
  const centre = nsdf[lag]
  const right = nsdf[lag + 1]
  if (left === undefined || right === undefined) {
    return { lag, value: centre }
  }
  const denominator = 2 * (2 * centre - left - right)
  if (denominator === 0) {
    return { lag, value: centre }
  }
  const delta = (right - left) / denominator
  return { lag: lag + delta, value: centre + ((right - left) * delta) / 4 }
}

export const detectPitch = (signal: Float32Array, sampleRate: number): PitchDetectionResult | null => {
  if (signal.length === 0 || rms(signal) < silenceRmsThreshold) {
    return null
  }

  const minLag = Math.max(2, Math.floor(sampleRate / maxDetectableHz))
  const maxLag = Math.min(Math.floor(signal.length / 2), Math.floor(sampleRate / minDetectableHz))
  if (maxLag <= minLag) {
    return null
  }

  const nsdf = normalizedSquareDifference(signal, maxLag + 1)
  const searchStart = Math.max(minLag, firstZeroCrossing(nsdf, maxLag))
  const maxima = findKeyMaxima(nsdf, searchStart, maxLag)
  if (maxima.length === 0) {
    return null
  }

  let tallest = nsdf[maxima[0]]
  for (const lag of maxima) {
    tallest = Math.max(tallest, nsdf[lag])
  }

  const threshold = nsdfPeakThreshold * tallest
  const chosen = maxima.find(lag => nsdf[lag] >= threshold) ?? maxima[0]
  const { lag, value } = interpolatePeak(nsdf, chosen)
  if (lag <= 0) {
    return null
  }

  return { frequency: sampleRate / lag, clarity: Math.max(0, Math.min(1, value)) }
}
